import { SlotItem } from '@stadionhq-v3/base-ui';
import {
  AVAILABLE_HEADER_PANELS,
  AVAILABLE_MAIN_PANELS,
  MATCH_CENTRE_BREAKPOINTS,
  MATCH_STATUS,
  MatchCentreBreakpoint,
  MatchStatus,
  ObjectValues,
  TABS_TYPES,
} from '@stadionhq-v3/core';
import pick from 'lodash/pick';

/**
  The config for panels in the site match centre
*/
export const PANELS = {
  ...AVAILABLE_HEADER_PANELS,
  ...pick(
    AVAILABLE_MAIN_PANELS,
    'FORM_GUIDE',
    'MATCH_NEWS',
    'MATCH_REPORT',
    'MATCH_STATS',
    'LINE_UPS',
    'AROUND_THE_GROUNDS',
    'COMMENTARY',
    'TABLES'
  ),
} as const;

export type Panel = ObjectValues<typeof PANELS>;

/**
  BASE COMPONENTS
*/
const formGuidePanel: SlotItem<Panel> = {
  slotName: PANELS.FORM_GUIDE,
  flex: { flexDirection: 'column' },
};
const matchNewsPanel: SlotItem<Panel> = {
  slotName: PANELS.MATCH_NEWS,
  flex: {
    flexDirection: 'column',
  },
};
const matchReportPanel: SlotItem<Panel> = {
  slotName: PANELS.MATCH_REPORT,
  flex: { flexGrow: 1, width: '100%' },
};

const commentaryPanel = (size: MatchCentreBreakpoint): SlotItem<Panel> => ({
  slotName: PANELS.COMMENTARY,
  flex: {
    flexDirection: 'column',
    ...(size === MATCH_CENTRE_BREAKPOINTS.S ? { width: '100%' } : {}),
    ...(size === MATCH_CENTRE_BREAKPOINTS.L
      ? { flexGrow: 1, flexBasis: 0 }
      : {}),
  },
});

const matchStatsPanel = (size: MatchCentreBreakpoint): SlotItem<Panel> => ({
  slotName: PANELS.MATCH_STATS,
  flex: {
    flexDirection: 'column',
    ...(size === MATCH_CENTRE_BREAKPOINTS.S ? { width: '100%' } : {}),
    ...(size === MATCH_CENTRE_BREAKPOINTS.L
      ? { flexGrow: 1, flexBasis: 0 }
      : {}),
  },
});

const lineUpsPanel = (size: MatchCentreBreakpoint): SlotItem<Panel> => ({
  slotName: PANELS.LINE_UPS,
  flex: {
    flexDirection: 'column',
    width: '100%',
    ...(size === MATCH_CENTRE_BREAKPOINTS.S
      ? {
          tokens: {
            gap: '--s-spacer-8',
          },
        }
      : {}),
    ...(size === MATCH_CENTRE_BREAKPOINTS.L
      ? {
          flexGrow: 1,
          flexBasis: 0,
          justifyContent: 'center',
        }
      : {}),
    tokens: {
      gap: '--s-spacer-8',
    },
  },
});

const aroundTheGroundsPanel = (
  size: MatchCentreBreakpoint
): SlotItem<Panel> => ({
  slotName: PANELS.AROUND_THE_GROUNDS,
  flex: {
    flexDirection: 'column',
    ...(size === MATCH_CENTRE_BREAKPOINTS.S ? { width: '100%' } : {}),
    ...(size === MATCH_CENTRE_BREAKPOINTS.L
      ? { flexGrow: 1, flexBasis: 0, width: '100%', maxWidth: '800px' }
      : {}),
    tokens: {
      gap: '--s-spacer-8',
    },
  },
});

const tablesPanel: SlotItem<Panel> = {
  slotName: PANELS.TABLES,
  flex: {
    flexDirection: 'column',
    width: '100%',
    flexGrow: 1,
    flexBasis: 0,
    maxWidth: '800px',
    tokens: {
      gap: '--s-spacer-8',
    },
  },
};

const aroundTheGroundsAndTablesTab: SlotItem = {
  slotName: 'Scores and Tables',
  slots: [aroundTheGroundsPanel(MATCH_CENTRE_BREAKPOINTS.L), tablesPanel],
  flex: {
    flexDirection: 'column',
    justifyContent: 'stretch',
    alignItems: 'center',
    tokens: {
      gap: '--s-spacer-8',
      paddingBottom: '--s-spacer-12',
    },
  },
};

const lineUpsAndScoresTabs = (size: MatchCentreBreakpoint): SlotItem => ({
  slotName: 'Line Ups and Scores Panel',
  tabs: [
    lineUpsPanel(MATCH_CENTRE_BREAKPOINTS.L),
    aroundTheGroundsAndTablesTab,
  ],
  flex: {
    ...(size === MATCH_CENTRE_BREAKPOINTS.S ? { width: '100%' } : {}),
    ...(size === MATCH_CENTRE_BREAKPOINTS.L
      ? { flexGrow: 1, flexBasis: 0 }
      : {}),
  },
  tabsType: TABS_TYPES.TABS,
});

/*
  PRE MATCH TAB
*/
const preMatchTab: SlotItem<MatchStatus> = {
  slotName: MATCH_STATUS.PRE_MATCH,
  slots: [matchNewsPanel, formGuidePanel],
  flex: {
    flexDirection: 'column',
    width: '100%',
    tokens: {
      gap: '--s-spacer-8',
      paddingBottom: '--s-spacer-12',
    },
  },
};

/**
  LIVE TAB
*/
const liveTabBase: SlotItem<MatchStatus> = {
  slotName: MATCH_STATUS.LIVE,
};

const liveTab: { [K in MatchCentreBreakpoint]: SlotItem<MatchStatus> } = {
  [MATCH_CENTRE_BREAKPOINTS.S]: {
    ...liveTabBase,
    slots: [
      {
        slotName: `${MATCH_STATUS.LIVE} Mobile`,
        tabs: [
          commentaryPanel(MATCH_CENTRE_BREAKPOINTS.S),
          matchStatsPanel(MATCH_CENTRE_BREAKPOINTS.S),
          lineUpsPanel(MATCH_CENTRE_BREAKPOINTS.S),
          aroundTheGroundsAndTablesTab,
        ],
        flex: {
          flexDirection: 'column',
          width: '100%',
          tokens: {
            gap: '--s-spacer-8',
            paddingBottom: '--s-spacer-12',
          },
        },
      },
    ],
  },
  [MATCH_CENTRE_BREAKPOINTS.L]: {
    ...liveTabBase,
    slots: [
      commentaryPanel(MATCH_CENTRE_BREAKPOINTS.L),
      matchStatsPanel(MATCH_CENTRE_BREAKPOINTS.L),
      lineUpsAndScoresTabs(MATCH_CENTRE_BREAKPOINTS.L),
    ],
    flex: {
      flexDirection: 'row',
      justifyContent: 'center',
      width: '100%',
      tokens: {
        gap: '--s-spacer-8',
        paddingBottom: '--s-spacer-12',
      },
    },
  },
};

/**
  POST MATCH TAB
*/
const postMatchTab: SlotItem<MatchStatus> = {
  slotName: MATCH_STATUS.POST_MATCH,
  slots: [matchReportPanel],
  flex: {
    flexDirection: 'column',
    width: '100%',
    tokens: {
      paddingBottom: '--s-spacer-12',
    },
  },
};

/**
  MATCH CENTRE MAIN CONFIG
*/
const matchCentreMainSlot = (size: MatchCentreBreakpoint): SlotItem => ({
  slotName: 'Match Status Tabs',
  tabs: [preMatchTab, liveTab[size], postMatchTab],
  tabsType: TABS_TYPES.SEGMENTED_CONTROLS,
});

export type MatchCentreConfig = {
  PANELS: typeof PANELS;
  HEADER: SlotItem | undefined;
  MAIN: { [K in MatchCentreBreakpoint]: SlotItem };
};

const matchCentreConfig: MatchCentreConfig = {
  PANELS,
  HEADER: undefined,
  MAIN: {
    [MATCH_CENTRE_BREAKPOINTS.S]: matchCentreMainSlot(
      MATCH_CENTRE_BREAKPOINTS.S
    ),
    [MATCH_CENTRE_BREAKPOINTS.L]: matchCentreMainSlot(
      MATCH_CENTRE_BREAKPOINTS.L
    ),
  },
};

export default matchCentreConfig;
